import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { AppConfig } from "../AppConfig";
import Graphic from "@arcgis/core/Graphic";
import * as geoprocessor from "@arcgis/core/rest/geoprocessor.js";
import { toast } from "react-toastify";
import { applyEdits, getMapView, getRelatedFeatures } from "./arcgisUtils";
import { getAGEToken } from "./authenticate";
import { getVersionName } from "./versioningUtils";
import { NauticalObjectValidationResult } from "../types/types";
import { FeatureClasses } from "../constants/NauticalObjects";
import { JaNei } from "../constants/nfsDomains";
import type { VersionInfoExtended, VersionInfo } from "@arcgis/core/versionManagement/support/jsonTypes";
import type CodedValueDomain from "@arcgis/core/layers/support/CodedValueDomain";

export enum MerkeplanStatus {
    Udefinert = 0,
    Plan = 1,
    IArbeid = 2,
    Avsluttet = 3,
    Reetablert = 4,
}

export enum NFSStatus {
    UNDEFINED = 0, // Udefinert
    PROJECTED_EXISTING = 1, // Prosjektert/Eksisterende
    PROJECTED = 2, // Prosjektert
    DEPLOYED_CHANGED = 3, // Utplassert/Endret
    PROJECTED_DISCONTINUED = 4, // Prosjektert nedlagt
    PROJECTED_REMOVED = 5, // Prosjektert fjernet
    REMOVED_CHANGED = 6, // Fjernet/Endret
    DISCONTINUED_CHANGED = 7, // Nedlagt/Endret
    INITIATED = 8, // Påbegynt
    PARTIALLY_COMPLETED = 9, // Delvis ferdig
    OPERATIVE = 20, // Operativ
    REMOVED = 21, // Fjernet
    DISCONTINUED = 23, // Nedlagt
}

export const getNFSStatusName = (status: NFSStatus): string => {
    switch (status) {
        case NFSStatus.UNDEFINED:
            return "Udefinert";
        case NFSStatus.PROJECTED_EXISTING:
            return "Prosjektert/Eksisterende";
        case NFSStatus.PROJECTED:
            return "Prosjektert";
        case NFSStatus.DEPLOYED_CHANGED:
            return "Utplassert/Endret";
        case NFSStatus.PROJECTED_DISCONTINUED:
            return "Prosjektert nedlagt";
        case NFSStatus.PROJECTED_REMOVED:
            return "Prosjektert fjernet";
        case NFSStatus.REMOVED_CHANGED:
            return "Fjernet/Endret";
        case NFSStatus.DISCONTINUED_CHANGED:
            return "Nedlagt/Endret";
        case NFSStatus.INITIATED:
            return "Påbegynt";
        case NFSStatus.PARTIALLY_COMPLETED:
            return "Delvis ferdig";
        case NFSStatus.OPERATIVE:
            return "Operativ";
        case NFSStatus.REMOVED:
            return "Fjernet";
        case NFSStatus.DISCONTINUED:
            return "Nedlagt";
        default:
            return "Udefinert";
    }
};

export const getStatusFieldName = (feature: Graphic): string | null => {
    const statusField =
        AppConfig.Layers.find((configLayer) => configLayer.Name === feature.layer?.id)?.StatusField ?? null;
    return statusField;
};
export const getNewStatus = (currentStatus: NFSStatus, newStatus: NFSStatus): NFSStatus => {
    switch (currentStatus) {
        case NFSStatus.PROJECTED:
        case NFSStatus.REMOVED:
        case NFSStatus.DISCONTINUED:
        case NFSStatus.PROJECTED_EXISTING:
        case NFSStatus.PROJECTED_DISCONTINUED:
        case NFSStatus.PROJECTED_REMOVED:
            return currentStatus;
        default:
            return newStatus;
    }
};

export const setNewStatusToFeature = (feature: Graphic, newStatus: NFSStatus): void => {
    const statusField = getStatusFieldName(feature);
    if (!statusField) return;
    const status = getNewStatus(feature.attributes[statusField], newStatus);
    feature.attributes[statusField] = status;
};

/** True for an IB attached to a HIB, or a Flytende merke attached to a Lysbøye. */
export const isHibOrLysboyeSecondary = (
    layerId: number | null | undefined,
    attributes: Record<string, any> | undefined
): boolean => {
    if (!attributes) return false;
    if (layerId === FeatureClasses.IB) return attributes.tilknyttet_hib == JaNei.Ja;
    if (layerId === FeatureClasses.Flytendemerke) return attributes.tilknyttet_lysboye == JaNei.Ja;
    return false;
};

export const syncSecondaryFeature = async (primaryFeature: Graphic, secondaryFeature: Graphic) => {
    const updateAttributes: Record<string, any> = {};

    if (secondaryFeature.attributes.objectid) {
        updateAttributes.objectid = secondaryFeature.attributes.objectid;
    } else {
        console.error("Secondary feature does not have an objectid. Cannot sync attributes.");
        return;
    }

    AppConfig.FeatureEditor.SecondaryFeatureCopyAttributeConfig.AllSecondary.forEach((attr) => {
        if (primaryFeature.attributes.hasOwnProperty(attr) && secondaryFeature.attributes.hasOwnProperty(attr)) {
            updateAttributes[attr] = primaryFeature.attributes[attr];
        }
    });

    if ((secondaryFeature.layer as FeatureLayer).layerId === FeatureClasses.IB) {
        AppConfig.FeatureEditor.SecondaryFeatureCopyAttributeConfig.IBSecondary.forEach((attr) => {
            if (primaryFeature.attributes.hasOwnProperty(attr) && secondaryFeature.attributes.hasOwnProperty(attr)) {
                updateAttributes[attr] = primaryFeature.attributes[attr];
            }
        });
    }

    if (isHibOrLysboyeSecondary((secondaryFeature.layer as FeatureLayer).layerId, secondaryFeature.attributes)) {
        AppConfig.FeatureEditor.SecondaryFeatureCopyAttributeConfig.HibLysboyeSecondary.forEach((attr) => {
            if (primaryFeature.attributes.hasOwnProperty(attr) && secondaryFeature.attributes.hasOwnProperty(attr)) {
                updateAttributes[attr] = primaryFeature.attributes[attr];
            }
        });
    }

    updateAttributes.nfs_status = primaryFeature.attributes.nfs_status;

    const updateFeature = new Graphic({
        attributes: updateAttributes,
    });

    const results = await applyEdits(secondaryFeature.layer as FeatureLayer, {
        updateFeatures: [updateFeature],
    });

    results.updateFeatureResults.forEach((result) => {
        if (result.error) {
            console.error("Error updating secondary feature:", result.error);
        }
    });
};

export const getNauticalImage = async (gisObjId: number): Promise<{ url: string | null; notFound: boolean }> => {
    const url = AppConfig.NFSAPI.BaseApiUrl + "fdvadmin/nauticalimage/" + gisObjId;
    const token = await getAGEToken();

    const response = await fetch(url, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (response.status === 404) {
        return { url: null, notFound: true };
    }
    if (!response.ok) {
        console.error("Failed to fetch nautical image:", response);
        return { url: null, notFound: false };
    }

    const blob = await response.blob();
    return { url: URL.createObjectURL(blob), notFound: false };
};

export const sendNaticalObjectsToMorild = async (
    nfsNauticalObjects: Graphic[],
    nauticalUser: number,
    gdbVersion?: string
): Promise<boolean> => {
    const lysSektorFeatures = await getRelatedFeatures(nfsNauticalObjects, "Lyssektor");
    const sektorflateFeatures = await getRelatedFeatures(lysSektorFeatures, "Lyssektor_F");

    const allNauticalObjects = [...nfsNauticalObjects, ...sektorflateFeatures];

    if (allNauticalObjects.length === 0) {
        console.warn("No nautical objects provided for Morild export.");
        return false;
    }

    const formattedData = formatNauticalObjectsForMorild(allNauticalObjects);

    const morildUrl = AppConfig.MorildExportServiceUrl;
    if (!morildUrl) {
        console.error("Morild export service URL is not configured.");
        return false;
    }
    try {
        const gpJob = await geoprocessor.submitJob(morildUrl, {
            nautiker: nauticalUser,
            data: formattedData,
            gdb_version: gdbVersion,
        });
        toast.info(`Starter eksport til Morild`, { toastId: "export-to-morild" });

        const jobResult = await gpJob.waitForJobCompletion({
            interval: 1000,
            statusCallback: (status: any) => {
                toast.update("export-to-morild", {
                    render: `Eksporterer... Status: ${status.jobStatus}\n Du kan lukke panelet, eksporten vil fortsette i bakgrunnen.`,
                    isLoading: true,
                });
            },
        });
        if (jobResult.jobStatus === "job-succeeded") {
            toast.update("export-to-morild", {
                render: "Eksport til Morild fullført!",
                type: "success",
                isLoading: false,
                autoClose: 5000,
            });
            return true;
        }
    } catch (error) {
        console.error("Error sending data to Morild:", error);
        return false;
    }
    return false;
};

interface MorildJsonData {
    lys: string[];
    ib: string[];
    sektorflate: string[];
    fastsjomerke: string[];
    flytendemerke: string[];
}

const formatNauticalObjectsForMorild = (nfsNauticalObjects: Graphic[]): string => {
    const formattedForMorildRequest: MorildJsonData = {
        lys: [],
        ib: [],
        sektorflate: [],
        fastsjomerke: [],
        flytendemerke: [],
    };

    nfsNauticalObjects.forEach((object) => {
        const layerId = (object.layer as FeatureLayer).layerId;
        switch (layerId) {
            case 0: // Lys
                formattedForMorildRequest.lys.push(object.attributes.globalid);
                break;

            case 1: // IB
                formattedForMorildRequest.ib.push(object.attributes.globalid);
                break;

            case 3: // Sektorflate
                formattedForMorildRequest.sektorflate.push(object.attributes.globalid);
                break;

            case 5: // Fastsjømerke
                formattedForMorildRequest.fastsjomerke.push(object.attributes.globalid);
                break;

            case 6: // Flytendemerke
                formattedForMorildRequest.flytendemerke.push(object.attributes.globalid);
                break;
        }
    });

    return JSON.stringify(formattedForMorildRequest);
};

export const getMorildUserOptions = async (): Promise<{ value: number | string; label: string }[]> => {
    const morildUserOptions: { value: number | string; label: string }[] = [];
    const morildLayer = new FeatureLayer({
        url: AppConfig.MorildLayerUrl + "0",
        outFields: ["*"],
    });

    if (!morildLayer) {
        console.error("Morild layer not found");
        return morildUserOptions;
    }

    await morildLayer.load();

    let domain = morildLayer.getFieldDomain("morild_nautiker");
    if (domain && domain.type === "coded-value") {
        const codedDomain = domain as CodedValueDomain;
        codedDomain.codedValues.forEach((codedValue) => {
            morildUserOptions.push({
                value: codedValue.code,
                label: codedValue.name,
            });
        });
    }
    return morildUserOptions;
};

const getMerkeplanLayer = (): FeatureLayer | null => {
    const mapView = getMapView();
    if (!mapView?.map) {
        return null;
    }

    return mapView.map.allLayers.find((l) => {
        return l instanceof FeatureLayer && `${l.url}/${l.layerId}` === AppConfig.MerkeplanAreaLayerUrl;
    }) as FeatureLayer;
};

export const addMerkeplan = async (version: VersionInfo | VersionInfoExtended, owner?: string) => {
    const merkeplanLayer = getMerkeplanLayer();
    const nameToStore = getVersionName(version);
    const versionGuid = version.versionIdentifier.guid;
    if (!merkeplanLayer) {
        console.error("Merkeplan layer not found");
        return;
    }

    const merkeplanFeature = {
        attributes: {
            merkeplan_navn: nameToStore,
            versjon_navn: versionGuid,
            eier: owner,
            status: MerkeplanStatus.Plan,
        },
    } as Graphic;

    const results = await applyEdits(merkeplanLayer, {
        addFeatures: [merkeplanFeature],
    });

    results.addFeatureResults.forEach((result) => {
        if (result.error) {
            console.error("Error adding merkeplan:", result.error);
        }
    });
};

export const getMerkeplanVersionNames = async (): Promise<string[]> => {
    const merkeplanLayer = getMerkeplanLayer();
    const versionNames: string[] = [];
    if (!merkeplanLayer) {
        console.error("Merkeplan layer not found");
        return versionNames;
    }
    const query = merkeplanLayer.createQuery();
    query.where = `1=1`;
    query.outFields = ["versjon_navn"];
    query.returnDistinctValues = true;
    const result = await merkeplanLayer.queryFeatures(query);
    result.features.forEach((feature) => {
        const versionName = feature.attributes["versjon_navn"];
        if (versionName) {
            versionNames.push(versionName);
        }
    });
    return versionNames;
};

const getMerkeplan = async (name: string): Promise<Graphic | null> => {
    const merkeplanLayer = getMerkeplanLayer();
    if (!merkeplanLayer) {
        console.error("Merkeplan layer not found");
        return null;
    }
    const query = merkeplanLayer.createQuery();
    query.where = `versjon_navn='${name}'`;
    query.outFields = ["objectid", "globalid"];
    const result = await merkeplanLayer.queryFeatures(query);
    if (result.features.length > 0) {
        return result.features[0];
    }
    return null;
};

export const deleteMerkeplan = async (versionInfo: VersionInfo | VersionInfoExtended) => {
    try {
        const merkeplanLayer = getMerkeplanLayer();

        if (!merkeplanLayer) {
            console.error("Merkeplan layer not found");
            return;
        }

        const versionGuid = versionInfo.versionIdentifier.guid;
        const merkeplanFeature = await getMerkeplan(versionGuid);
        if (!merkeplanFeature) {
            return;
        }

        const results = await applyEdits(merkeplanLayer, {
            deleteFeatures: [merkeplanFeature],
        });

        results.deleteFeatureResults.forEach((result) => {
            if (result.error) {
                console.error("Error deleting merkeplan:", result.error);
            }
        });
    } catch (error) {
        console.error("Error in deleteMerkeplan:", error);
    }
};

export const getValidationResult = async (
    nfsFeatureGlobalId: string,
    layerId: number,
    gdbVersion: string | null,
    validationContext: "PostToDefault" | "SendToSKSD" = "PostToDefault"
): Promise<NauticalObjectValidationResult> => {
    let url =
        AppConfig.NFSAPI.BaseApiUrl +
        `sksd/validation/${layerId}/${nfsFeatureGlobalId}?validationContext=${validationContext}`;
    if (gdbVersion) {
        url = url.concat(`&gdbVersion=${gdbVersion}`);
    }

    const token = await getAGEToken();

    const response = await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
        },
    });

    if (!response.ok) {
        const errorJson = await response.json();
        throw new Error(`HTTP error! status: ${response.status} ${errorJson}`);
    }

    const result = (await response.json()) as NauticalObjectValidationResult;
    return result;
};
